import { createFileRoute, Link } from "@tanstack/react-router";
import { AuthorityBadges } from "@/components/AuthorityBadges";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Check, Sparkles, Palette, Zap, Target, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteHeader, SiteFooter } from "@/components/blog/SiteHeader";
import sierraStairs from "@/assets/sierra-stairs.webp";
import heroPeople from "@/assets/about-hero.jpg";

import avatarElaine from "@/assets/testimonials/avatar-elaine-B3rNOmMI.png";
import avatarAhriana from "@/assets/testimonials/avatar-ahriana-BmR4_NC6.png";
import avatarCarrie from "@/assets/testimonials/avatar-carrie-DtFoRfBZ.png";
import coachWorking from "@/assets/coach-working.webp";
import coachWorkingVideo from "@/assets/coach-working.mp4";
import trusted1 from "@/assets/trusted/coach-1.jpg";
import trusted2 from "@/assets/trusted/coach-2.jpg";
import trusted3 from "@/assets/trusted/coach-3.jpg";
import trusted4 from "@/assets/trusted/coach-4.jpg";
import trusted5 from "@/assets/trusted/coach-5.jpg";
import trusted6 from "@/assets/trusted/coach-6.jpg";
import trusted7 from "@/assets/trusted/coach-7.jpg";
import trusted8 from "@/assets/trusted/coach-8.jpg";
import trusted9 from "@/assets/trusted/coach-9.jpg";
import trusted10 from "@/assets/trusted/coach-10.jpg";
import trusted11 from "@/assets/trusted/coach-11.jpg";
import trusted12 from "@/assets/trusted/coach-12.jpg";
import trusted13 from "@/assets/trusted/coach-13.webp";
import trusted14 from "@/assets/trusted/coach-14.webp";
import trusted15 from "@/assets/trusted/coach-15.png";
const trustedImages = [trusted1, trusted2, trusted3, trusted4, trusted5, trusted6, trusted7, trusted8, trusted9, trusted10, trusted11, trusted12, trusted13, trusted14, trusted15];

const SITE = "https://threedaylaunch.com";
const bookingUrl = "/book";
const TITLE = "About Three Day Launch — Custom Websites by Sierra Janisse";
const DESCRIPTION =
  "Meet Sierra Janisse, founder of Three Day Launch. A Chicago-based designer building custom coaching websites — launched in 3 days, built to book more clients.";
const OG_IMAGE = `${SITE}${sierraStairs}`;

const faqs = [
  {
    q: "How long does a 3-day website launch actually take?",
    a: "Three working days from kickoff to live site. We do a strategy call up front, then a dedicated design day, and your custom coaching website goes live on day three. No 6-week timelines, no agency limbo.",
  },
  {
    q: "Who is Three Day Launch for?",
    a: "Coaches, consultants, and experts who work with clients in some kind of live support. This could look like 1:1 coaching, group programs, masterminds, or hybrid offers. If you're invested in the people you serve and ready to look the part online, you're our kind of client.",
  },
  {
    q: "Where is Sierra based and do you work remotely?",
    a: "Sierra is based in Chicago, IL and works with clients all over the world. The entire Three Day Launch process is all remote; so meetings and design reviews are held virtually.",
  },
  {
    q: "What makes Three Day Launch different from Squarespace, Wix, or an agency?",
    a: "Templates like Squarespace and Wix make your site look like every other coach online. Agencies take months and cost five figures. Three Day Launch is the middle path: a real custom site, designed by a human, launched in 3 days, at a fraction of the agency price.",
  },
];

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/about` },
      { property: "og:type", content: "profile" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/about` },
      { rel: "preload", as: "image", href: sierraStairs, fetchPriority: "high" } as unknown as { rel: string; href: string },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          url: `${SITE}/about`,
          name: TITLE,
          description: DESCRIPTION,
          mainEntity: {
            "@type": "Organization",
            name: "Three Day Launch",
            url: SITE,
            description:
              "Custom coaching websites designed and launched in 3 days. Based in Chicago, serving coaches worldwide.",
            founder: {
              "@type": "Person",
              name: "Sierra Janisse",
              jobTitle: "Founder & Designer",
              image: OG_IMAGE,
              worksFor: { "@type": "Organization", name: "Three Day Launch" },
            },
            areaServed: "Worldwide",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Chicago",
              addressRegion: "IL",
              addressCountry: "US",
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Sierra Janisse",
          jobTitle: "Founder & Design Lead",
          image: OG_IMAGE,
          url: `${SITE}/about`,
          worksFor: {
            "@type": "Organization",
            name: "Three Day Launch",
            url: SITE,
          },
          address: {
            "@type": "PostalAddress",
            addressLocality: "Chicago",
            addressRegion: "IL",
            addressCountry: "US",
          },
          knowsAbout: [
            "Custom website design for coaches",
            "Coaching website conversion",
            "Brand-led web design",
            "Squarespace alternative for coaches",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: AboutPage,
});

const beliefs = [
  {
    icon: Palette,
    title: "Custom, never templated",
    body: "Every site is designed from a blank canvas around your brand, voice, and offer.",
  },
  {
    icon: Zap,
    title: "Fast, 3-day turnaround",
    body: "No 6-week timelines. We design, revise, and launch in 72 hours, start to finish.",
  },
  {
    icon: Target,
    title: "Built to convert",
    body: "Every page is structured around one goal: turning visitors into aligned clients.",
  },
  {
    icon: User,
    title: "One designer, end-to-end",
    body: "You work directly with Sierra. No agency handoffs, no junior designers, no middlemen.",
  },
];


const aboutTestimonials = [
  {
    quote:
      "Always pleasant, upbeat, and open to feedback, making the process smooth and enjoyable. Your creativity and attention to detail really shone through, and you were able to translate my ideas into a beautiful, functional website.",
    name: "Elaine E.",
    avatar: avatarElaine,
  },
  {
    quote:
      "You are the best thing that's happened for my online business. Now, this website feels like ME. Like people can actually feel who I am and see what I'm up to! YOU TOTALLY ROCK!",
    name: "Ahriana P.",
    avatar: avatarAhriana,
  },
  {
    quote:
      "I love how you asked great questions and had creative ways of figuring out what we wanted. The end result really shows that part of us. You helped us send the message we were hoping to send.",
    name: "Carrie S.",
    avatar: avatarCarrie,
  },
];

function AboutPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader showAbout />

      <main>
        <Hero />
        <WhoWeServe />
        <Beliefs />
        <Stats />
        <div className="relative overflow-hidden bg-background">
          <CursorGlow opacity={0.22} />
          <OurStory />
          <MeetFounder />
          <Niches />
          <FAQ />
          <FinalCTA />
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-secondary text-secondary-foreground pt-24 pb-20 md:pt-32 md:pb-24">
      <img
        src={heroPeople}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/45" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/40 to-black/60 pointer-events-none" />

      <div className="relative mx-auto max-w-4xl px-6 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-wider border border-white/15">
          <Sparkles className="h-3.5 w-3.5 text-highlight" />
          YOUR NEW WEBSITE BFF
        </div>
        <h1 className="mt-6 text-4xl md:text-6xl leading-[1.1] font-bold">
          About <span className="text-primary">Three Day Launch</span> custom coaching websites by <span className="text-highlight">Sierra Janisse.</span>
        </h1>
        <p className="mt-6 text-xl md:text-2xl font-semibold text-white/90">
          Custom coaching websites, launched in just 3 days.
        </p>
        <p className="mt-4 text-lg md:text-xl text-white/75 max-w-2xl mx-auto">
          Three Day Launch designs and builds custom websites for coaches who are tired of waiting months and spending thousands for a site that still doesn't feel like them.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Button asChild size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
            <Link to="/contact">
              Book a free strategy call <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white h-14 px-8 rounded-full text-base">
            <Link to="/services">See what's included</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

/**
 * Renders the poster image immediately, and only mounts the <video> when the
 * element scrolls into view AND we're on a viewport wider than 768px.
 * Mobile users get the poster image only — saves ~600KB on cellular.
 */
function LazyVideo({
  src,
  poster,
  className,
  width,
  height,
}: {
  src: string;
  poster: string;
  className?: string;
  width: number;
  height: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Skip the video on small viewports — poster is plenty.
    if (window.matchMedia("(max-width: 768px)").matches) return;
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShowVideo(true);
            obs.disconnect();
          }
        });
      },
      { rootMargin: "200px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={containerRef} className={className}>
      {showVideo ? (
        <video
          src={src}
          poster={poster}
          className="w-full h-full object-cover"
          width={width}
          height={height}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
        />
      ) : (
        <img
          src={poster}
          alt="A coach working at her desk"
          className="w-full h-full object-cover"
          width={width}
          height={height}
          loading="lazy"
          decoding="async"
        />
      )}
    </div>
  );
}

function WhoWeServe() {
  const placeholders = trustedImages;
  return (
    <section className="relative py-24 md:py-32 bg-muted/40 overflow-hidden">
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-highlight/10 blur-3xl pointer-events-none" />

      <div className="relative mb-16 md:mb-20">
        <div className="text-center px-6">
          <h3 className="text-xl md:text-2xl font-bold text-foreground">
            Trusted By <span className="text-primary">100's</span> of Coaches <span className="text-primary">+</span> Experts
          </h3>
        </div>
        <div className="mt-8 relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
          <div className="flex gap-6 w-max animate-[marquee_40s_linear_infinite]">
            {[...placeholders, ...placeholders].map((src, idx) => (
              <div
                key={idx}
                className="h-28 w-24 md:h-32 md:w-28 shrink-0 rounded-2xl overflow-hidden bg-muted border border-border"
                aria-hidden="true"
              >
                <img src={src} alt="" className="h-full w-full object-cover object-top" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-20 items-center">
          {/* Image side */}
          <div className="relative">
            <LazyVideo
              src={coachWorkingVideo}
              poster={coachWorking}
              width={800}
              height={1000}
              className="relative rounded-[2rem] overflow-hidden shadow-card-soft aspect-[4/5] bg-muted"
            />

            {/* Floating badge */}
            <div className="hidden sm:flex absolute -bottom-6 -right-4 md:-right-8 items-center gap-3 bg-card border border-border rounded-2xl px-5 py-4 shadow-card-soft max-w-[16rem]">
              <p className="text-sm font-semibold leading-snug">
                For conscious creators selling transformation.
              </p>
            </div>

            </div>

          {/* Text side */}
          <div>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
              Every coach deserves a website{" "}
              <span className="text-primary">worthy of your vision.</span>
            </h2>

            <div className="mt-8 space-y-5 text-muted-foreground text-lg leading-relaxed">
              <p>
                When we say <span className="text-foreground font-semibold">"coach"</span> we mean an expert who works with clients in some form of live support. Traditional 1:1 work, or 1:many programs in all sorts of formats.
              </p>
              <p>
                Whether you call yourself a "coach" or not, you're someone who's truly invested in the success of your people.
              </p>
            </div>

            <p className="mt-8 text-xl md:text-2xl font-semibold text-foreground leading-snug border-l-4 border-primary pl-5">
              And that makes you <em>our</em> kind of people.
            </p>

            <div className="mt-10">
              <Button
                asChild
                size="lg"
                className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
              >
                <a href={bookingUrl}>
                  Work with us <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MeetFounder() {
  return (
    <section id="meet-founder" className="py-24 bg-background pt-[45px] scroll-mt-24">
      <div className="mx-auto max-w-7xl px-6">

        <div className="grid md:grid-cols-[1fr_1.2fr] gap-10 md:gap-14 items-start">
          <div className="relative rounded-3xl overflow-hidden shadow-card-soft bg-muted">
            <img
              src={sierraStairs}
              alt="Sierra Janisse, Founder and Designer at Three Day Launch, sitting on stairs"
              className="w-full h-full object-cover"
              width={900}
              height={1125}
              fetchPriority="high"
              decoding="async"
            />
          </div>

          <div>
            <h2 className="text-4xl md:text-5xl font-bold leading-tight">
              Hi, I'm Sierra Janisse.
            </h2>
            <div className="mt-2 text-primary font-semibold text-sm uppercase tracking-wider">
              Founder & Design Lead
            </div>

            <div className="mt-6 space-y-5 text-muted-foreground text-lg leading-relaxed">
              <p>
                I'm the founder of Three Day Launch and the creative director behind every site we launch. When you work with us, you work directly with me. No handoffs, no junior designers, no agency middlemen.
              </p>
              <p>
                I've launched hundreds of websites for businesses across every industry, from coaches and consultants to interior designers and hair stylists, accountants and dog groomers. Along the way, I noticed the same thing kept happening: brilliant coaches were stuck with websites that didn't match the quality of their work, because the "good" option always seemed to cost thousands and take months.
              </p>
              <p>
                As a Chicago-based website designer working with coaches across the globe, I kept seeing the same two extremes: a Squarespace or Wix template that looked like every other coach online, or a five-figure agency build that took six months to launch. Neither served the coaches I wanted to work with. The ones doing real, life-changing work with their clients.
              </p>
              <p>
                I built Three Day Launch as a genuine third option: a fully custom alternative to coaching templates, designed by a human, delivered in 72 hours, at a price that actually makes sense for a growing practice. See <Link to="/services" className="text-primary underline-offset-4 hover:underline font-medium">what's included in a 3-day launch</Link>, browse <Link to="/" hash="jealous" className="text-primary underline-offset-4 hover:underline font-medium">recent coaching website projects</Link>, or read more about our <Link to="/web-design-for-coaches" className="text-primary underline-offset-4 hover:underline font-medium">approach to web design for coaches</Link>.
              </p>
              <p>
                I started Three Day Launch because I believe every coach deserves a website they're genuinely proud of, without a 6-week timeline or a five-figure invoice. Based in Chicago, working with clients worldwide.
              </p>
            </div>
            <div className="mt-8">
              <Button
                asChild
                size="lg"
                className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
              >
                <Link to="/contact">
                  Work with Sierra <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            <AuthorityBadges />
          </div>
        </div>
      </div>
    </section>
  );
}

function OurStory() {
  return (
    <section className="py-24 bg-muted/40 pt-[45px] pb-[90px]">
      <div className="mx-auto max-w-3xl px-6">
        <div className="text-center mt-[45px]">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            Our Story
          </div>
          <h2 className="mt-4 text-3xl md:text-4xl font-bold">
            Why Three Day Launch exists.
          </h2>
        </div>
        <div className="mt-10 space-y-5 text-lg text-muted-foreground leading-relaxed">
          <p>
            Most coaches we talk to have the same story. They booked an agency, paid a deposit, sat through endless discovery calls, then waited months for a site that still didn't quite feel like them. Or they tried to DIY it on a template and ended up with something that looks like every other coach on the internet.
          </p>
          <p>
            We built Three Day Launch to be the opposite of that. One strategy call, one focused design day, one launch day. A real custom website, built by a human designer, live in 72 hours. No bloat, no upsells, no surprises.
          </p>
          <p>
            The goal isn't just a pretty site. It's a site that books discovery calls while you sleep.
          </p>
        </div>
      </div>
    </section>
  );
}

function Beliefs() {
  return (
    <section className="py-24 bg-background">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            What we believe
          </div>
          <h2 className="mt-4 text-3xl md:text-4xl font-bold">
            Four principles behind every coaching site we ship.
          </h2>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {beliefs.map((b) => (
            <div key={b.title} className="bg-card border border-border rounded-3xl p-6 shadow-card-soft">
              <div className="h-12 w-12 rounded-2xl bg-primary/15 text-primary flex items-center justify-center">
                <b.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-lg font-bold">{b.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const stats = [
    { n: "500+", l: "Websites launched", target: 500, suffix: "+" },
    { n: "3 days", l: "Average launch time", target: 3, suffix: " days" },
    { n: "Chicago", l: "HQ, clients worldwide" },
    { n: "1:1", l: "Direct designer access" },
  ];
  const ref = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setActive(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <section ref={ref} className="py-20 bg-secondary text-secondary-foreground">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((s) => (
            <div key={s.l}>
              <div className="text-4xl md:text-5xl font-bold text-primary">
                {s.target !== undefined ? (
                  <CountUp target={s.target} suffix={s.suffix ?? ""} active={active} />
                ) : (
                  s.n
                )}
              </div>
              <div className="mt-2 text-sm uppercase tracking-wider text-white/70">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CountUp({ target, suffix, active, duration = 1600 }: { target: number; suffix: string; active: boolean; duration?: number }) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  const display = target % 1 === 0 ? Math.round(value) : value.toFixed(1);
  return <>{display}{suffix}</>;
}

function Niches() {
  return (
    <section className="py-24 bg-background">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            Kind words
          </div>
          <h2 className="mt-4 text-3xl md:text-4xl font-bold">
            What clients say about working with Sierra.
          </h2>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {aboutTestimonials.map((t) => (
            <figure
              key={t.name}
              className="bg-card border border-border rounded-3xl p-8 shadow-card-soft flex flex-col"
            >
              <div
                className="text-primary text-7xl leading-none font-black mb-2 font-sans"
              >
                "
              </div>
              <blockquote className="text-muted-foreground leading-relaxed flex-1">
                {t.quote}
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="h-12 w-12 rounded-full object-cover"
                  width={48}
                  height={48}
                  loading="lazy"
                  decoding="async"
                />
                <div className="font-semibold">{t.name}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section className="py-24 bg-muted/40 pt-[45px] pb-[90px]">
      <div className="mx-auto max-w-3xl px-6">
        <div className="text-center mt-[45px]">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            FAQ
          </div>
          <h2 className="mt-4 text-3xl md:text-4xl font-bold">
            Frequently asked questions.
          </h2>
        </div>
        <div className="mt-10 space-y-4">
          {faqs.map((f) => (
            <details
              key={f.q}
              className="group bg-card border border-border rounded-2xl px-6 py-5 shadow-card-soft open:shadow-md transition-shadow"
            >
              <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-semibold text-lg">
                <span>{f.q}</span>
                <span className="text-primary text-2xl leading-none transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-4 text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-24 bg-background">
      <div className="mx-auto max-w-4xl px-6 text-center">
        <h2 className="text-4xl md:text-5xl font-bold">
          Ready to launch your site in <span className="text-primary">3 days?</span>
        </h2>
        <p className="mt-5 text-lg text-muted-foreground max-w-2xl mx-auto">
          Tell us about your coaching business and we'll show you exactly what your custom site could look like.
        </p>
        <ul className="mt-8 inline-flex flex-col sm:flex-row gap-x-8 gap-y-2 text-left">
          {["Custom designed", "Launched in 3 days", "Built to convert"].map((t) => (
            <li key={t} className="flex items-center gap-2 text-muted-foreground">
              <span className="h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center">
                <Check className="h-3 w-3 text-primary" />
              </span>
              {t}
            </li>
          ))}
        </ul>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Button asChild size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
            <Link to="/contact">
              Book a free call <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="h-14 px-8 rounded-full text-base">
            <Link to="/blog">Read the blog</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

function CursorGlow({ color = "var(--primary)", opacity = 0.18 }: { color?: string; opacity?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    let x = 0, y = 0;
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
      if (!raf) {
        raf = requestAnimationFrame(() => {
          el.style.setProperty("--gx", `${x}px`);
          el.style.setProperty("--gy", `${y}px`);
          raf = 0;
        });
      }
    };
    const parent = el.parentElement;
    parent?.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      parent?.removeEventListener("mousemove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 transition-opacity duration-300"
      style={{
        background: `radial-gradient(600px circle at var(--gx, 50%) var(--gy, 50%), color-mix(in oklab, ${color} ${opacity * 100}%, transparent), transparent 60%)`,
      }}
    />
  );
}

